import styled from '@emotion/styled';
import { TFunction } from 'react-i18next';
import { FormikProps } from 'formik';
import { FormData, SpeakerData } from '../feature/webinar-creator-wizard';
import { StyledButton, StyledTitle } from '@selfserve/react-ui';
import moment from 'moment-timezone';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SIDE_IMAGE_SMALL } from '@selfserve/react-utils';

interface Props {
  t: TFunction<'translation', undefined>;
  formik: FormikProps<FormData>;
}

const StyledSideScreenContainer = styled.div(() => ({
  flex: `0 0 50%`,
  padding: '64px 0px',
  display: 'flex',
  justifyContent: 'center',
  '& *': {
    fontFamily: 'Lato !important',
  },
  color: 'white',
}));

const StyledBackgroundImage = styled.div(() => ({
  position: 'fixed',
  right: '0',
  top: '0',
  zIndex: '-1',
  filter: 'blur(64px)',
  background: 'url(assets/blur_blue.png)',
  height: '100%',
  width: '100%',
}));

const StyledLandingPageContainer = styled.div(({ theme }: { theme?: any }) => ({
  borderRadius: '8px',
  [theme.breakpoints.only('xl')]: {
    width: `720px`,
  },
  [theme.breakpoints.only('lg')]: {
    width: `512px`,
  },
  [theme.breakpoints.only('md')]: {
    width: `432px`,
  },
  [theme.breakpoints.down('md')]: {
    width: `calc(100% - 132px)`,
  },
  height: 'calc(100vh - 136px)',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '3px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    width: '3px',
    backgroundColor: 'white',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-button': {
    width: '3px',
    height: '4px',
  },
  '&.dark': {
    background: 'rgba(17, 17, 17, 1)',
    '& .styledFooter': {
      background: 'rgba(34, 34, 34, 1)',
    },
  },
  '&.light': {
    background: theme.palette.common.white,
    '& .styledSectionContainer': {
      color: 'rgba(102, 102, 102, 1)',
    },
    '& .styledFooter': {
      background: 'rgba(244, 244, 244, 1)',
    },
    '& .sectionTitle, .styledFooter': {
      color: 'rgba(51, 51, 51, 1)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#CCCCCC',
    },
  },
}));

const StyledBannerContainer = styled.div(
  ({ image, theme }: { image?: string; theme?: any }) => ({
    borderRadius: '8px 8px 0 0',
    width: '100%',
    minHeight: '300px',
    maxHeight: '300px',
    overflow: 'hidden',
    background: `linear-gradient(360deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%), url("${image}")`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& .styledTitle': {
      textAlign: 'center',
      maxWidth: '80%',
      lineHeight: '50px',
    },
    '& .buttonGroup': {
      display: 'flex',
      gap: '8px',
      marginTop: '10px',

      '& button': {
        display: 'block',
        width: '122px',
        height: '24px',
        fontSize: '10px',
        lineHeight: '12px',
        color: theme.palette.primary.contrastText,
        border: 'none',

        '&:first-of-type': {
          backgroundColor: theme.palette.primary.main,
        },

        '&:last-of-type': {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
      },
    },
    '& .styledCounterGroup': {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
      '& .styledCounterChild': {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        minWidth: '22px',
        '& :last-of-type': {
          fontSize: '9px',
          fontWeight: '400',
          lineHeight: '16px',
          color: 'rgba(255, 255, 255, 0.8)',
        },
      },
      '& .styledCounterDivider:last-of-type': {
        display: 'none',
      },
    },
  })
);

const StyledLogoHeader = styled.div(({ theme }: { theme?: any }) => ({
  borderRadius: '8px 8px 0 0',
  [theme.breakpoints.only('xl')]: {
    width: `720px`,
  },
  [theme.breakpoints.only('lg')]: {
    width: `512px`,
  },
  [theme.breakpoints.only('md')]: {
    width: `432px`,
  },
  [theme.breakpoints.down('md')]: {
    width: `calc(100% - 132px)`,
  },
  height: '34px',
  background: `linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)`,
  position: 'fixed',
  top: '64px',
  display: 'flex',
  alignItems: 'center',
}));

const StyledLogoContainer = styled.div(({ image }: { image?: string }) => ({
  width: '62px',
  height: '16px',
  background: `url("${image}")`,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  marginLeft: '10px',
}));

const StyledDuration = styled(StyledTitle)(({ theme }: { theme?: any }) => ({
  fontSize: theme.typography.pxToRem(11),
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '12px',
  letterSpacing: '0em',
}));

const StyledSectionContainer = styled.div(({ theme }: { theme?: any }) => ({
  paddingTop: '40px',
  paddingLeft: '66px',
  paddingRight: '66px',
  flexGrow: '1',
}));

const StyledSectionTitle = styled(StyledTitle)(
  ({ theme }: { theme?: any }) => ({
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '20px',
    letterSpacing: '0em',
    marginBottom: '16px',
  })
);

const StyledDescriptionSection = styled.div(
  ({ theme, light }: { theme?: any; light?: boolean }) => ({
    fontSize: theme.typography.pxToRem(10),
    lineHeight: '12px',
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: '40px',
    marginTop: '6px',
    color: light ? 'rgb(102, 102, 102)' : 'rgb(204, 204, 204)',
  })
);

const StyledSpeakersContainer = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  flexWrap: 'wrap',
  marginBottom: '10px',
}));

const StyledSpeakerContainer = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  [theme.breakpoints.only('xl')]: {
    width: '104.2px',
  },
  [theme.breakpoints.only('lg')]: {
    width: '115px',
  },
  [theme.breakpoints.only('md')]: {
    width: '140.5px',
  },
}));

const StyledSpeakerImage = styled.div(
  ({ speaker, theme }: { speaker: SpeakerData; theme?: any }) => ({
    borderRadius: '5px',
    ...(speaker.photo
      ? {
          background: `url("${speaker.photo}")`,
        }
      : {
          backgroundColor: 'rgb(112, 112, 112)',
          '&::after': {
            content: `"${speaker.firstName[0].toUpperCase()}${speaker.lastName[0].toUpperCase()}"`,
            visibility: 'visible',
            fontFamily: 'Lato',
            color: 'white',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: '700',
            margin: 'calc(50% - 0.7rem)',
            top: 'calc(50% - 10px)',
            position: 'relative',
          },
        }),
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    marginBottom: '4px',
    [theme.breakpoints.only('xl')]: {
      width: '104.2px',
      height: '104.2px',
    },
    [theme.breakpoints.only('lg')]: {
      width: '115px',
      height: '115px',
    },
    [theme.breakpoints.only('md')]: {
      width: '140.5px',
      height: '140.5px',
    },
  })
);

const StyledSpeakerFullname = styled.div(
  ({ theme, light }: { theme?: any; light?: boolean }) => ({
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '14px',
    color: light ? 'rgb(51, 51, 51)' : 'white',
  })
);

const StyledSpeakerDescription = styled.div(
  ({ theme, light }: { theme?: any; light?: boolean }) => ({
    fontSize: theme.typography.pxToRem(10),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '12px',
    color: light ? 'rgb(102, 102, 102)' : 'rgb(204, 204, 204)',
  })
);

const StyledFooter = styled.div(({ theme }: { theme?: any }) => ({
  color: theme.palette.common.white,
  fontSize: theme.typography.pxToRem(8),
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '10px',
  height: '78px',
  paddingTop: '28px',
  paddingLeft: '24px',
  paddingBottom: '32px',
  backgroundColor: 'rgba(34, 34, 34, 1)',
}));

export const WebinarSidescreen = ({ t, formik }: Props) => {
  const {
    values: {
      logo,
      banner,
      theme,
      description,
      time,
      date,
      timezone,
      speakers,
      name,
      duration,
    },
  } = formik;
  const timerId = useRef<ReturnType<typeof setInterval> | null>(null);
  const [timeLeft, setTimeLeft] = useState<
    { amount: number; name: string }[] | null
  >(null);

  const getDatesOfEvent = useMemo(() => {
    const parts = (time as string).match(
      /(\d+):(\d+) (\w+)/
    ) as RegExpMatchArray;
    const hours = parts[1];
    const minutes = parseInt(parts[2], 10);
    const partOfDay = parts[3];
    const startDateInternal = new Date(date as number).toLocaleString('en-us', {
      month: 'numeric',
      year: 'numeric',
      day: 'numeric',
    });

    const startDate = moment(
      `${startDateInternal} ${hours}:${minutes} ${partOfDay}`,
      'M/D/YYYY hh:mm a'
    );

    const endDate = moment(startDate).add(
      parseInt(duration as string),
      'minutes'
    );

    return {
      startDate: startDate,
      endDate: endDate,
    };
  }, [date, time, duration, timezone]);

  useEffect(() => {
    if (timerId.current) clearInterval(timerId.current);
    timerId.current = setInterval(() => {
      const durationLeft = moment.duration(
        getDatesOfEvent.startDate.diff(moment())
      );

      const tzOffset =
        moment()
          .tz(timezone as string)
          .utcOffset() - moment().utcOffset();

      durationLeft.add(tzOffset * 60 * 1000);

      setTimeLeft([
        {
          amount: Math.floor(durationLeft.asDays()),
          name: 'days',
        },
        {
          amount: durationLeft.hours(),
          name: 'hrs',
        },
        {
          amount: durationLeft.minutes(),
          name: 'min',
        },
        {
          amount: durationLeft.seconds(),
          name: 'sec',
        },
      ]);
    }, 1000);
  }, [getDatesOfEvent, date, time, duration, timezone]);

  const StyledSpeaker = (speaker: SpeakerData, theme: string) => {
    return (
      <StyledSpeakerContainer>
        <StyledSpeakerImage speaker={speaker} />
        <StyledSpeakerFullname
          light={theme !== 'dark'}
        >{`${speaker.firstName} ${speaker.lastName}`}</StyledSpeakerFullname>
        <StyledSpeakerDescription
          light={theme !== 'dark'}
        >{`${speaker.title}, ${speaker.company}`}</StyledSpeakerDescription>
      </StyledSpeakerContainer>
    );
  };

  return (
    <StyledSideScreenContainer>
      <StyledBackgroundImage />
      <StyledLandingPageContainer className={theme}>
        <StyledLogoHeader>
          <StyledLogoContainer image={logo} />
        </StyledLogoHeader>
        <StyledBannerContainer image={banner}>
          <StyledTitle isSmall className="styledTitle">
            {name}
          </StyledTitle>
          <StyledDuration>
            {getDatesOfEvent.startDate.format('MMM DD, h:mm A')} &gt;{' '}
            {getDatesOfEvent.endDate.format('MMM DD, h:mm A')}{' '}
            {moment()
              .tz(timezone as string)
              .format('z')}
          </StyledDuration>
          <div className="buttonGroup">
            <StyledButton disabled>Register</StyledButton>
            <StyledButton disabled>Enter the Site</StyledButton>
          </div>
          <div className="styledCounterGroup">
            {timeLeft?.map((timeLeft) => (
              <>
                <div className="styledCounterChild">
                  <div>{timeLeft.amount}</div>
                  <div>{timeLeft.name}</div>
                </div>
                <div className="styledCounterDivider">:</div>
              </>
            ))}
          </div>
        </StyledBannerContainer>
        <StyledSectionContainer className="styledSectionContainer">
          <StyledSectionTitle className="sectionTitle">
            {t('webinar-wizard.sidescreen-about')}
          </StyledSectionTitle>
          <StyledDescriptionSection
            dangerouslySetInnerHTML={{ __html: `${description}` }}
            light={theme === 'light'}
          />
          <StyledSectionTitle className="sectionTitle">
            {t('webinar-wizard.sidescreen-speakers')}
          </StyledSectionTitle>
          <StyledSpeakersContainer>
            {speakers.map((speaker) => StyledSpeaker(speaker, theme as string))}
          </StyledSpeakersContainer>
        </StyledSectionContainer>
        <StyledFooter className="styledFooter">
          {t('webinar-wizard.sidescreen-footer')}
        </StyledFooter>
      </StyledLandingPageContainer>
    </StyledSideScreenContainer>
  );
};
